import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import PricingCards from "@/components/PricingCards";
import { trackEvent } from "@/lib/analytics";
import {
  MagnifyingGlass, Broadcast, Buildings, UsersThree, Path, TrendUp,
  ArrowRight, Lightning, Target, Brain, MapPin, Handshake,
} from "@phosphor-icons/react";

const JOURNEY = [
  { icon: Brain, label: "Compétence" },
  { icon: MagnifyingGlass, label: "Recherche IA" },
  { icon: Broadcast, label: "Signaux marché" },
  { icon: Target, label: "Opportunités" },
  { icon: Buildings, label: "Entreprise" },
  { icon: UsersThree, label: "Décideurs" },
  { icon: Handshake, label: "Nouveaux comptes" },
  { icon: TrendUp, label: "Prochain contrat" },
];

const FEATURES = [
  { icon: Brain, title: "Recherche en langage naturel", desc: "Décrivez un consultant comme vous le feriez à l'oral. L'IA structure compétences, séniorité et zone de mobilité." },
  { icon: Broadcast, title: "Signaux de recrutement", desc: "Chaque besoin de recrutement détecté est la preuve qu'une entreprise a un besoin. Nous le transformons en opportunité." },
  { icon: Target, title: "Score d'opportunité", desc: "Un score de correspondance priorise les entreprises les plus pertinentes pour vos consultants disponibles." },
  { icon: UsersThree, title: "Les bons décideurs", desc: "Accédez directement aux interlocuteurs clés : CTO, VP Engineering, responsables de programme." },
  { icon: MapPin, title: "Zone de mobilité", desc: "Filtrez par ville et rayon. Positionnez vos consultants là où le marché a réellement besoin d'eux." },
  { icon: Path, title: "Anticipation de l'intercontrat", desc: "Détectez la prochaine mission avant la fin de la précédente. Donnez à vos commerciaux une longueur d'avance." },
];

export default function Landing() {
  return (
    <div className="relative z-10 bg-bg text-fg min-h-screen">
      {/* Nav */}
      <header className="glass border-b border-border sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-5 h-16 flex items-center justify-between">
          <Logo size="text-2xl" />
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted">
            <a href="#fonctionnement" className="hover:text-fg transition-colors">Fonctionnement</a>
            <a href="#tarifs" className="hover:text-fg transition-colors">Tarifs</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="text-sm text-muted hover:text-fg px-3 py-2">Connexion</Link>
            <Link to="/register" data-testid="nav-register"
              onClick={() => trackEvent("cta_signup", { placement: "nav" })}
              className="text-sm bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift whitespace-nowrap">
              <span className="sm:hidden">29 €/mois</span><span className="hidden sm:inline">S'abonner — 29 €/mois</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden dot-grid">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10), transparent 65%)" }} />
        <div className="max-w-[1200px] mx-auto px-5 pt-24 pb-20 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-1.5 mb-8 fade-up">
              <Lightning size={14} className="text-accent" weight="fill" />
              <span className="overline !text-fg/70">Intelligence commerciale pour les ESN</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[1.02] mb-6 fade-up" style={{ animationDelay: "80ms" }}>
              Moins d'intercontrat. <span className="brand-ko">Plus de clients.</span>
            </h1>
            <p className="text-xl sm:text-2xl font-display font-bold text-fg mb-6 fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-anchor">
              29 €/mois. Résiliable à tout moment. <span className="brand-ko">Toutes les opportunités de mission.</span>
            </p>
            <p className="text-lg text-muted leading-relaxed max-w-2xl mb-4 fade-up" style={{ animationDelay: "160ms" }}>
              Décrivez le profil ou les compétences de votre consultant.
              <span className="text-fg font-medium"> interKO</span> repère les entreprises qui recherchent ce type d'expertise,
              priorise les opportunités les plus pertinentes et vous aide à contacter directement les bons décideurs.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 fade-up" style={{ animationDelay: "200ms" }} data-testid="hero-price">
              <div className="inline-flex items-baseline gap-2">
                <span className="font-display text-5xl font-extrabold text-accent">29 €</span>
                <span className="text-fg/80 text-lg font-semibold">/ mois HT</span>
              </div>
              <div className="text-sm text-muted">
                <span className="text-fg font-semibold">Sans engagement · Résiliable à tout moment</span><br className="hidden sm:block" />
                <span className="text-fg/70"> Accès illimité aux opportunités de mission</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-8 fade-up" style={{ animationDelay: "240ms" }}>
              <Link to="/register" data-testid="hero-primary-cta"
                onClick={() => trackEvent("cta_signup", { placement: "hero" })}
                className="bg-accent text-black font-semibold px-6 py-3.5 rounded-md hover-lift flex items-center gap-2">
                S'abonner pour 29 €/mois <ArrowRight size={18} weight="bold" />
              </Link>
              <a href="#fonctionnement" data-testid="hero-secondary-cta"
                className="text-fg border border-border px-6 py-3.5 rounded-md hover-lift hover:border-accent/40">
                Voir comment ça fonctionne
              </a>
            </div>
          </div>

          {/* fake search preview */}
          <div className="mt-16 surface rounded-xl p-2 max-w-3xl fade-up" style={{ animationDelay: "320ms" }}>
            <div className="flex items-center gap-3 bg-surface-2 rounded-lg px-4 py-4 border border-border">
              <MagnifyingGlass size={20} className="text-accent shrink-0" />
              <span className="text-fg/80 text-sm sm:text-base font-mono">
                DevOps senior, AWS, Kubernetes et Terraform autour de Paris dans un rayon de 50 km.
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 px-1">
              {[{ n: "Novaxis Cloud", s: 100 }, { n: "Assuréa Digital", s: 99 }, { n: "Tecnorail", s: 97 }].map((c, i) => (
                <div key={i} className="bg-surface-2 rounded-md p-3 border border-border">
                  <div className="text-xs text-muted truncate">{c.n}</div>
                  <div className="font-mono text-accent text-lg font-bold">{c.s}</div>
                  <div className="overline !text-[0.6rem]">Score d'opportunité</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section id="fonctionnement" className="border-y border-border bg-surface/40">
        <div className="max-w-[1200px] mx-auto px-5 py-16">
          <div className="overline mb-3">Le parcours interKO</div>
          <h2 className="font-display text-3xl font-bold mb-3">De la compétence au prochain contrat</h2>
          <p className="text-muted max-w-2xl mb-12">L'annonce de recrutement n'est pas le produit. C'est un signal. interKO le transforme en opportunité commerciale.</p>
          <div className="flex flex-wrap items-center gap-3">
            {JOURNEY.map((j, i) => {
              const Icon = j.icon;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="surface rounded-lg px-4 py-3 flex items-center gap-2.5 hover-lift">
                    <Icon size={20} weight="duotone" className="text-accent" />
                    <span className="text-sm text-fg whitespace-nowrap">{j.label}</span>
                  </div>
                  {i < JOURNEY.length - 1 && <ArrowRight size={16} className="text-muted hidden sm:block" />}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-[1200px] mx-auto px-5 py-24">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-3">Ce que fait interKO</div>
          <h2 className="font-display text-4xl font-bold mb-4">Transformez les besoins de recrutement en opportunités commerciales</h2>
          <p className="text-muted">Détectez les entreprises qui ont besoin des compétences de vos consultants. Moins d'intercontrat. Plus de clients.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="surface rounded-lg p-6 hover-lift fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-11 h-11 rounded-md bg-accent/10 border border-accent/25 flex items-center justify-center mb-4">
                  <Icon size={22} weight="duotone" className="text-accent" />
                </div>
                <h3 className="font-display text-lg font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-muted leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Deuxième cas d'usage — intelligence commerciale / développement business */}
        <div data-testid="business-dev-block" className="mt-14 surface accent-glow border border-accent/40 rounded-xl p-8 sm:p-10 fade-up">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-14 h-14 rounded-lg bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0">
              <Handshake size={28} weight="duotone" className="text-accent" />
            </div>
            <div>
              <div className="overline mb-2 !text-accent">Intelligence commerciale · Développement business</div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold mb-3 leading-tight">
                Au-delà de l'intercontrat — <span className="brand-ko">développez votre portefeuille client.</span>
              </h3>
              <p className="text-muted leading-relaxed max-w-3xl">
                Chaque signal de recrutement détecté est aussi une piste commerciale. interKO vous aide à identifier
                les entreprises en croissance, à cibler les bons décideurs et à ouvrir de nouveaux comptes avant vos
                concurrents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote band */}
      <section className="border-y border-border bg-surface/40">
        <div className="max-w-[1000px] mx-auto px-5 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
            « Anticipez la prochaine mission <span className="brand-ko">avant la fin de la précédente.</span> »
          </h2>
          <p className="text-muted mt-4 font-mono text-sm">Votre prochain contrat commence par un signal.</p>
        </div>
      </section>

      {/* Pricing */}
      <section id="tarifs" className="max-w-[1200px] mx-auto px-5 py-24">
        <div className="max-w-2xl mb-12">
          <div className="overline mb-3">Tarifs</div>
          <h2 className="font-display text-4xl font-bold mb-4">Toutes les opportunités de mission. Un seul abonnement.</h2>
          <p className="text-muted"><span className="text-fg font-semibold">29 €/mois. Sans engagement.</span> Accédez sans limite aux opportunités de mission, identifiez vos prochains clients et développez votre activité grâce à une plateforme de prospection intelligente.</p>
        </div>
        <PricingCards />
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-[1200px] mx-auto px-5 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <Logo size="text-xl" />
            <p className="text-muted text-sm mt-2">Ne laissez plus vos consultants entre deux missions.</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted">
            <Link to="/login" className="hover:text-fg">Connexion</Link>
            <Link to="/register" onClick={() => trackEvent("cta_signup", { placement: "footer" })} className="hover:text-fg">Commencer</Link>
            <span className="font-mono text-xs">interKO.io</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
